import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { associateApi, contractorApi } from '../lib/api';
import { useUnit } from '../contexts/UnitContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { Plus, X, Pencil, Check, Undo2, Trash2, ArrowUp, ArrowDown } from 'lucide-react';
import AssociateProfileDialog from '../components/AssociateProfileDialog';
import SkillMatrix from './SkillMatrix';

/* The columns the register can be ordered by. `key` is what the server sorts
   on, so the sort happens over the WHOLE filtered set and not just the page
   the browser happens to be holding. */
const COLUMNS = [
  { key: 'code',            label: 'Code',        w: 'w-[108px]' },
  { key: 'name',            label: 'Name' },
  { key: 'contractor_name', label: 'Contractor' },
  { key: 'department',      label: 'Department' },
  { key: 'designation',     label: 'Designation' },
  { key: 'level',           label: 'Level',       w: 'w-[88px]' },
  { key: 'salary',          label: 'Salary',      align: 'right', w: 'w-[104px]' },
  { key: 'doj',             label: 'Date of Joining', w: 'w-[132px]' },
];

const rupees = (n) => `₹${Math.round(n || 0).toLocaleString('en-IN')}`;
const today = () => new Date().toISOString().slice(0, 10);

/* Designation is the JOB, level is the PROFICIENCY, and they are two columns
   because they move independently: the same OPERATOR goes L1 → L2 → L3 as the
   Skill Matrix scores them without ever ceasing to be an OPERATOR. Rendering
   them as one string ("OPERATOR L2") would have made that impossible to
   filter, sort or total on. */
const LEVELS = [1, 2, 3, 4];

/* Which levels a designation may be held at, from the server rather than
   hard-coded here: the thirteen approved designations are L1-L3, and CHAMPS
   is L4 and nothing else. */
const levelsFor = (meta, designation) =>
  meta?.designation_levels?.[designation] || [1, 2, 3];

/* L4 is CHAMPS and reads as a rank rather than a rung, so it is marked out
   from the three skill levels instead of sitting in the same grey pill. */
function LevelPill({ level }) {
  if (!level) return <span className="text-[#C9CFDC]">—</span>;
  const champs = Number(level) === 4;
  return (
    <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold border ${
      champs ? 'bg-[#FFF4E5] text-[#8A4B00] border-[#FFD9A8]'
             : 'bg-[#EFF3FB] text-[#2F4A77] border-[#D9E2F3]'}`}>
      L{level}
    </span>
  );
}

/* A blank date of joining is the norm right now — the attendance exports the
   471 came from carry no joining date, so HR fills them in over time. Shown as
   a muted dash rather than an empty cell so the column does not look broken. */
const Dash = () => <span className="text-[#C9CFDC]">—</span>;

/* The register shows a contractor's SHORT name — the legal names run to four
   words and wrapped every row to triple height. Falls back to the stored name
   for a contractor the meta call has not caught up with yet. */
const lockNote = (meta, what) => (meta?.can_reassign ? ''
  : `Changing an associate's ${what} is restricted to the HR head — it changes `
    + 'who is billed for them and moves them on every headcount and cost report.');

const contractorLabel = (meta, row) =>
  (meta?.contractors || []).find((c) => c.id === row.contractor_id)?.short_name
  || row.contractor_name;

function SortHeader({ col, sort, direction, onSort }) {
  const active = sort === col.key;
  const Icon = direction === 'asc' ? ArrowUp : ArrowDown;
  return (
    <th className={`py-2 px-2 ${col.align === 'right' ? 'text-right' : 'text-left'} ${col.w || ''}`}>
      <button onClick={() => onSort(col.key)} data-testid={`assoc-sort-${col.key}`}
        className={`inline-flex items-center gap-1 uppercase text-xs tracking-wide
                    ${active ? 'text-[#2563EB] font-bold' : 'hover:text-[#2563EB]'}`}>
        {col.label}
        {active && <Icon className="w-3 h-3" strokeWidth={2.6} />}
      </button>
    </th>
  );
}

/* ------------------------------------------------------------------ */
/* Add row — one line, always visible at the top of the register.      */
/* Associates come and go daily, so adding one must not cost a dialog. */
/* ------------------------------------------------------------------ */
function AddRow({ meta, unit, onAdded }) {
  const blank = { name: '', contractor_id: '', department: '', designation: '', level: 1, salary: '', code: '', doj: '' };
  const [f, setF] = useState(blank);
  const [busy, setBusy] = useState(false);
  const nameRef = useRef(null);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  /* Picking CHAMPS means L4 and picking anything else means L1-L3, so the
     level follows the designation rather than being left on a value the
     server would then reject. */
  useEffect(() => {
    const allowed = levelsFor(meta, f.designation);
    if (!allowed.includes(Number(f.level))) setF((p) => ({ ...p, level: allowed[0] }));
  }, [f.designation]);                              // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    if (!f.name.trim()) { toast.error('Name is required'); nameRef.current?.focus(); return; }
    if (!f.contractor_id) { toast.error('Pick a contractor'); return; }
    if (!f.department) { toast.error('Pick a department'); return; }
    if (!f.designation.trim()) { toast.error('Designation is required'); return; }
    setBusy(true);
    try {
      const r = await associateApi.add({
        ...f, unit, salary: Number(f.salary || 0), level: Number(f.level || 1),
        code: f.code.trim() || undefined, doj: f.doj || undefined,
      });
      toast.success(`${r.data.name} added as ${r.data.code}`);
      // Contractor and department are kept: a day's hiring is usually one
      // contractor sending several people into the same department.
      setF({ ...blank, contractor_id: f.contractor_id, department: f.department });
      nameRef.current?.focus();
      onAdded();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not add'); }
    finally { setBusy(false); }
  };

  const cell = 'border border-[#E6E9F0] rounded-lg px-2 py-1.5 text-sm w-full bg-white';
  return (
    <tr className="bg-[#F7FAFF] border-b-2 border-[#E6E9F0]"
        onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}>
      <td className="py-2 px-2">
        <input value={f.code} onChange={set('code')} placeholder="auto" className={cell}
               title="Leave blank and the next free code for this contractor at this plant is used"
               data-testid="assoc-add-code" />
      </td>
      <td className="py-2 px-2">
        <input ref={nameRef} value={f.name} onChange={set('name')} placeholder="Full name"
               className={cell} data-testid="assoc-add-name" />
      </td>
      <td className="py-2 px-2">
        <select value={f.contractor_id} onChange={set('contractor_id')} className={cell}
                data-testid="assoc-add-contractor">
          <option value="">Contractor…</option>
          {meta.contractors.map((c) => (
            <option key={c.id} value={c.id}>{c.short_name || c.name}</option>
          ))}
        </select>
      </td>
      <td className="py-2 px-2">
        <select value={f.department} onChange={set('department')} className={cell}
                data-testid="assoc-add-department">
          <option value="">Department…</option>
          {meta.departments.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
      </td>
      <td className="py-2 px-2">
        {/* From the shared pool, not free text: typing it meant every
            misspelling became a new role and the register could never be
            grouped. The pool is set in Settings → Masters, beside the staff
            designations. */}
        <select value={f.designation} onChange={set('designation')} className={cell}
                data-testid="assoc-add-designation">
          <option value="">Designation…</option>
          {meta.designations.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
      </td>
      <td className="py-2 px-2">
        {/* Offers only the levels this designation can be held at, so the
            invalid pairings the server refuses — CHAMPS at L2, OPERATOR at
            L4 — are never selectable in the first place. */}
        <select value={f.level} onChange={set('level')} className={cell}
                data-testid="assoc-add-level">
          {levelsFor(meta, f.designation).map((n) => (
            <option key={n} value={n}>L{n}</option>
          ))}
        </select>
      </td>
      <td className="py-2 px-2">
        <input value={f.salary} onChange={set('salary')} type="number" placeholder="0"
               className={`${cell} text-right`} data-testid="assoc-add-salary" />
      </td>
      <td className="py-2 px-2">
        <input value={f.doj} onChange={set('doj')} type="date" className={cell}
               data-testid="assoc-add-doj" />
      </td>
      <td className="py-2 px-2 text-right">
        <Button size="sm" onClick={submit} disabled={busy} data-testid="assoc-add-save">
          <Plus className="w-3.5 h-3.5 mr-1" />Add
        </Button>
      </td>
    </tr>
  );
}

/* ------------------------------------------------------------------ */
/* One register row, read mode and edit mode.                          */
/* ------------------------------------------------------------------ */
function Row({ r, meta, canEdit, canRemove, onChanged, onOpenProfile }) {
  const [editing, setEditing] = useState(false);
  const [f, setF] = useState(r);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setF(r); }, [r]);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const save = async () => {
    setBusy(true);
    try {
      await associateApi.update(r.id, {
        code: f.code, name: f.name, contractor_id: f.contractor_id,
        department: f.department, designation: f.designation,
        level: Number(f.level || 1),
        salary: Number(f.salary || 0), doj: f.doj || '',
      });
      toast.success('Saved');
      setEditing(false);
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };

  const markLeft = async () => {
    if (!window.confirm(`Mark ${r.name} (${r.code}) as left today?\n\nThe record is kept so the exit still counts towards associate attrition.`)) return;
    try { await associateApi.exit(r.id, { last_working_day: today() }); toast.success(`${r.name} marked as left`); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not record the exit'); }
  };
  const bringBack = async () => {
    try { await associateApi.update(r.id, { status: 'Active' }); toast.success(`${r.name} is back on the register`); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not restore'); }
  };
  const erase = async () => {
    if (!window.confirm(`Delete ${r.name} (${r.code}) completely?\n\nUse this only for a row typed by mistake — a real leaver should be marked as left instead, so attrition can count them.`)) return;
    try { await associateApi.remove(r.id); toast.success('Record deleted'); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  const cell = 'border border-[#E6E9F0] rounded-lg px-2 py-1 text-sm w-full';
  const left = r.status === 'Left';

  if (editing) {
    return (
      <tr className="bg-[#FFFBEB] border-b border-[#F2F4F8]"
          onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') { setF(r); setEditing(false); } }}>
        <td className="py-1.5 px-2"><input value={f.code} onChange={set('code')} className={cell} /></td>
        <td className="py-1.5 px-2"><input value={f.name} onChange={set('name')} className={cell} /></td>
        {/* Contractor and department are locked unless this user may reassign.
            Moving somebody changes who is billed for them and shifts them on
            every headcount and cost report, so it belongs to the HR head —
            the rest of the row stays editable by whoever works the register.
            Disabled rather than hidden, so it is clear the field exists and
            who to ask. */}
        <td className="py-1.5 px-2">
          <select value={f.contractor_id} onChange={set('contractor_id')} className={cell}
                  disabled={!meta.can_reassign} title={lockNote(meta, 'contractor')}
                  data-testid="assoc-edit-contractor">
            {meta.contractors.map((c) => <option key={c.id} value={c.id}>{c.short_name || c.name}</option>)}
          </select>
        </td>
        <td className="py-1.5 px-2">
          <select value={f.department} onChange={set('department')} className={cell}
                  disabled={!meta.can_reassign} title={lockNote(meta, 'department')}
                  data-testid="assoc-edit-department">
            {meta.departments.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </td>
        <td className="py-1.5 px-2">
          <select value={f.designation} onChange={set('designation')} className={cell}
                  data-testid="assoc-edit-designation">
            {meta.designations.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </td>
        {/* Editable in its own right: a Skill Matrix result moves somebody
            from L1 to L2 without their designation changing at all. */}
        <td className="py-1.5 px-2">
          <select value={f.level || 1} onChange={set('level')} className={cell}
                  data-testid="assoc-edit-level">
            {levelsFor(meta, f.designation).map((n) => <option key={n} value={n}>L{n}</option>)}
          </select>
        </td>
        <td className="py-1.5 px-2"><input value={f.salary} onChange={set('salary')} type="number" className={`${cell} text-right`} /></td>
        <td className="py-1.5 px-2"><input value={f.doj || ''} onChange={set('doj')} type="date" className={cell} /></td>
        <td className="py-1.5 px-2 text-right whitespace-nowrap">
          <button onClick={save} disabled={busy} title="Save" className="text-[#15654B] px-1"><Check className="w-4 h-4" /></button>
          <button onClick={() => { setF(r); setEditing(false); }} title="Cancel" className="text-[#98A1B2] px-1"><X className="w-4 h-4" /></button>
        </td>
      </tr>
    );
  }

  return (
    <tr className={`border-b border-[#F2F4F8] ${left ? 'bg-[#FAFAFB] text-[#98A1B2]' : ''}`}
        data-testid={`assoc-row-${r.code}`}>
      <td className="py-1.5 px-2 font-mono text-xs text-[#5A6377]">{r.code}</td>
      {/* The NAME opens the read-only profile. It is a separate action from
          the pencil (inline edit) and the cross (exit) on the right of the
          row — clicking a name must never put the row into edit mode, and
          vice versa. */}
      <td className="py-1.5 px-2 font-medium text-[#11182B] whitespace-nowrap">
        <button type="button" onClick={() => onOpenProfile(r)}
                data-testid={`assoc-name-${r.code}`}
                title={`View ${r.name}'s profile`}
                className={`text-left hover:text-[#2563EB] hover:underline ${left ? 'line-through' : ''}`}>
          {r.name}
        </button>
        {left && <span className="ml-2 text-[10px] uppercase tracking-wide text-[#B42318]">left {r.last_working_day || ''}</span>}
      </td>
      {/* The short name, not the legal one. "A.M Employment Services Pvt. Ltd"
          wrapped to four lines and tripled the height of every row; the full
          name is still one hover away. */}
      <td className="py-1.5 px-2 text-[#5A6377] whitespace-nowrap" title={r.contractor_name}>
        {contractorLabel(meta, r)}
        {r.is_own_payroll && (
          <span className="ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-[#EAF7F0] text-[#15654B] border border-[#CDEBDB]">
            on-roll
          </span>
        )}
      </td>
      {/* Department stays on one line; designation is the only column allowed
          to wrap. Holding every column on one line pushed Salary and Date of
          Joining off the right edge, and designation is both the longest
          ("BARREL OPERATOR-2ND FLOOR") and the one least often read. */}
      <td className="py-1.5 px-2 text-[#5A6377] whitespace-nowrap">{r.department}</td>
      <td className="py-1.5 px-2 text-[#5A6377]"
          title={r.source_designation !== r.designation ? `Export says: ${r.source_designation}` : ''}>
        {r.designation}
      </td>
      {/* "—" means NOT ASSESSED YET. It is not L1, not zero and not a missing
          value to be filled in with a guess: L1/L2/L3 are results the Skill
          Matrix produces, so until it has scored this person there is nothing
          truthful to show. L4 (CHAMPS) is the exception — it comes from the
          approved structure, not from an assessment. */}
      <td className="py-1.5 px-2" data-testid={`assoc-level-${r.code}`}
          title={!r.level
            ? 'Not assessed yet — the Skill Matrix sets L1, L2 or L3. This is not L1.'
            : (r.level_source === 'approved_sheet' ? 'Set by the approved Associate structure'
              : r.level_source === 'skill_matrix' ? 'From the Skill Matrix assessment' : '')}>
        <LevelPill level={r.level} />
      </td>
      <td className="py-1.5 px-2 text-right tabular-nums">{r.salary ? rupees(r.salary) : <Dash />}</td>
      <td className="py-1.5 px-2">{r.doj || <Dash />}</td>
      <td className="py-1.5 px-2 text-right whitespace-nowrap">
        {canEdit && !left && (
          <button onClick={() => setEditing(true)} title="Edit" data-testid={`assoc-edit-${r.code}`}
                  className="text-[#5A6377] hover:text-[#2563EB] px-1"><Pencil className="w-3.5 h-3.5" /></button>
        )}
        {canRemove && !left && (
          <button onClick={markLeft} title="Mark as left" data-testid={`assoc-exit-${r.code}`}
                  className="text-[#C2151F] hover:opacity-70 px-1"><X className="w-4 h-4" /></button>
        )}
        {canRemove && left && (
          <>
            <button onClick={bringBack} title="Bring back onto the register"
                    className="text-[#15654B] hover:opacity-70 px-1"><Undo2 className="w-3.5 h-3.5" /></button>
            <button onClick={erase} title="Delete the record completely"
                    className="text-[#C2151F] hover:opacity-70 px-1"><Trash2 className="w-3.5 h-3.5" /></button>
          </>
        )}
      </td>
    </tr>
  );
}

/* ------------------------------------------------------------------ */
/* Contractor view — what each supplier costs this plant.              */
/*                                                                     */
/* READ ONLY. Adding a contractor is not a daily act like adding an    */
/* associate: it declares a new company the plants may draw labour     */
/* from, and every associate filed under it inherits that. So it lives */
/* in Settings → Masters with units and departments, admin only.       */
/* ------------------------------------------------------------------ */
function ContractorView({ unit }) {
  const [rows, setRows] = useState(null);

  const load = useCallback(async () => {
    try { setRows((await contractorApi.list(unit)).data); }
    catch (e) { toast.error('Failed to load the contractor master'); }
  }, [unit]);
  useEffect(() => { load(); }, [load]);

  const total = (rows || []).reduce((a, c) => a + c.headcount, 0);
  const cost = (rows || []).reduce((a, c) => a + c.monthly_cost, 0);

  return (
    <div className="bg-white border border-[#E6E9F0] rounded-xl p-5">
      <h3 className="font-medium text-[#11182B] mb-1">Contractors</h3>
      <p className="text-xs text-[#98A1B2] mb-4">
        What each supplier costs {unit ? <b>{unit}</b> : 'across every plant'}, counting{' '}
        <b>active</b> associates only. Contractors themselves are added and renamed in{' '}
        <Link to="/settings" className="text-[#2563EB] underline">Settings → Masters</Link>.
      </p>

      {!rows ? <p className="text-[#98A1B2]">Loading…</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#EFF1F6] text-[#5A6377] text-xs uppercase">
              <tr>
                <th className="py-2 px-2 text-left">Contractor</th>
                <th className="py-2 px-2 text-left w-32">Short name</th>
                <th className="py-2 px-2 text-right w-28">Associates</th>
                <th className="py-2 px-2 text-right w-36">Monthly cost</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id} className="border-b border-[#F2F4F8]" data-testid={`contractor-row-${c.id}`}>
                  <td className="py-2 px-2 font-medium text-[#11182B]">
                    {c.name}
                    {c.is_own_payroll && (
                      <span className="ml-2 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-[#EAF7F0] text-[#15654B] border border-[#CDEBDB]">
                        own payroll
                      </span>
                    )}
                  </td>
                  <td className="py-2 px-2 text-[#5A6377]">{c.short_name}</td>
                  <td className="py-2 px-2 text-right tabular-nums font-semibold text-[#172554]">{c.headcount}</td>
                  <td className="py-2 px-2 text-right tabular-nums">{rupees(c.monthly_cost)}</td>
                </tr>
              ))}
              <tr className="bg-[#F7FAFF] font-semibold text-[#172554]">
                <td className="py-2 px-2" colSpan={2}>Total</td>
                <td className="py-2 px-2 text-right tabular-nums">{total}</td>
                <td className="py-2 px-2 text-right tabular-nums">{rupees(cost)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */

export default function Associates() {
  const { activeUnit, isAllUnits } = useUnit();
  const { hasPerm } = useAuth();
  const canAdd = hasPerm('associate.create');
  const canEdit = hasPerm('associate.edit');
  const canRemove = hasPerm('associate.delete');

  // Opened straight from a dashboard bar: ?department=Production arrives
  // pre-filtered, the same way the employees list reads its query string.
  const [params] = useSearchParams();
  const [meta, setMeta] = useState(null);
  // Which associate's profile is open. Held as an ID, never a name — two
  // associates can share a name and the popup must be certain whose record
  // it is showing.
  const [profileId, setProfileId] = useState(null);
  const [data, setData] = useState(null);
  const [contractorId, setContractorId] = useState('');
  const [department, setDepartment] = useState(params.get('department') || '');
  const [designation, setDesignation] = useState(params.get('designation') || '');
  const [level, setLevel] = useState(params.get('level') || '');
  const [status, setStatus] = useState('Active');
  const [q, setQ] = useState('');
  const [sort, setSort] = useState('name');
  const [direction, setDirection] = useState('asc');

  // In "All Units" mode the unit param is stripped by the axios interceptor,
  // so the register spans every plant a full-scope role can see.
  const unitParam = isAllUnits ? undefined : (activeUnit || undefined);

  useEffect(() => {
    associateApi.meta().then((r) => setMeta(r.data))
      .catch(() => toast.error('Failed to load contractors and departments'));
  }, []);

  // Switching plants or filters fires a read while the previous one may still
  // be in flight; without this guard a slow earlier reply repaints the table
  // with the wrong plant's people.
  const token = useRef(0);
  const load = useCallback(async () => {
    const mine = ++token.current;
    try {
      const r = await associateApi.list({
        unit: unitParam, contractor_id: contractorId || undefined,
        department: department || undefined, designation: designation || undefined,
        level: level || undefined,
        status, q: q || undefined, sort, direction,
      });
      if (token.current === mine) setData(r.data);
    } catch (e) {
      if (token.current === mine) toast.error('Failed to load the associate register');
    }
  }, [unitParam, contractorId, department, designation, level, status, q, sort, direction]);

  // Debounced so typing in the search box does not fire a read per keystroke.
  useEffect(() => { const t = setTimeout(load, 180); return () => clearTimeout(t); }, [load]);

  const onSort = (key) => {
    if (key === sort) { setDirection((d) => (d === 'asc' ? 'desc' : 'asc')); return; }
    setSort(key);
    // Money and dates are almost always wanted highest/latest first; names and
    // codes alphabetically.
    setDirection(key === 'salary' || key === 'doj' ? 'desc' : 'asc');
  };

  const refresh = useCallback(() => {
    load();
    associateApi.meta().then((r) => setMeta(r.data)).catch(() => {});
  }, [load]);

  const summary = data?.summary;
  const missingDoj = useMemo(
    () => (data?.rows || []).filter((r) => !r.doj).length, [data]);

  const filterSel = 'border border-[#E6E9F0] rounded-lg px-2.5 py-1.5 text-sm bg-white';

  return (
    <div className="flex h-screen app-shell">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header />
        <main className="flex-1 overflow-y-auto p-6 space-y-5">
          <div>
            <h1 className="font-display text-2xl font-bold tracking-[-0.02em] text-[#172554]">Associates</h1>
            <p className="text-sm text-[#5A6377]">
              The shop floor — contract and own-payroll workers, held separately from staff.
              Sort by any column, and add or remove a name in one line.
            </p>
          </div>

          <Tabs defaultValue="register">
            <TabsList>
              <TabsTrigger value="register" data-testid="assoc-tab-register">Register</TabsTrigger>
              <TabsTrigger value="contractors" data-testid="assoc-tab-contractors">Contractors</TabsTrigger>
              {/* Where a level comes from. Beside the register rather than in
                  Settings, because assessing people is day-to-day HR work on
                  the same list of names. */}
              <TabsTrigger value="skill" data-testid="assoc-tab-skill">Skill Matrix</TabsTrigger>
            </TabsList>

            <TabsContent value="register" className="space-y-5 mt-4">
              {/* Rollups for exactly what the filters are showing */}
              {summary && (
                <div className="flex flex-wrap gap-2">
                  <div className="bg-white border border-[#E6E9F0] rounded-lg px-3 py-2 text-sm">
                    <b className="text-[#172554]">{summary.headcount}</b>
                    <span className="text-[#5A6377]"> {status === 'Left' ? 'left' : 'associates'}</span>
                  </div>
                  <div className="bg-white border border-[#E6E9F0] rounded-lg px-3 py-2 text-sm">
                    <b className="text-[#172554]">{rupees(summary.monthly_cost)}</b>
                    <span className="text-[#5A6377]"> / month</span>
                  </div>
                  {summary.by_contractor.slice(0, 6).map((b) => (
                    <div key={b.key} className="bg-white border border-[#E6E9F0] rounded-lg px-3 py-2 text-sm">
                      <b className="text-[#172554]">{b.headcount}</b>
                      <span className="text-[#5A6377]"> {b.label}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Filter bar */}
              <div className="bg-white border border-[#E6E9F0] rounded-xl p-4 flex flex-wrap items-end gap-3">
                <div>
                  <div className="text-xs uppercase text-[#98A1B2] mb-1">Search</div>
                  <Input value={q} onChange={(e) => setQ(e.target.value)} className="w-56 text-sm"
                         placeholder="Name, code, designation…" data-testid="assoc-search" />
                </div>
                <div>
                  <div className="text-xs uppercase text-[#98A1B2] mb-1">Contractor</div>
                  <select value={contractorId} onChange={(e) => setContractorId(e.target.value)}
                          className={filterSel} data-testid="assoc-filter-contractor">
                    <option value="">All contractors</option>
                    {(meta?.contractors || []).map((c) => (
                      <option key={c.id} value={c.id}>{c.short_name || c.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <div className="text-xs uppercase text-[#98A1B2] mb-1">Department</div>
                  <select value={department} onChange={(e) => setDepartment(e.target.value)}
                          className={filterSel} data-testid="assoc-filter-department">
                    <option value="">All departments</option>
                    {(meta?.departments || []).map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
                <div>
                  <div className="text-xs uppercase text-[#98A1B2] mb-1">Status</div>
                  <select value={status} onChange={(e) => setStatus(e.target.value)}
                          className={filterSel} data-testid="assoc-filter-status">
                    <option value="Active">On the register</option>
                    <option value="Left">Left</option>
                    <option value="">Everyone</option>
                  </select>
                </div>
                <div>
                  <div className="text-xs uppercase text-[#98A1B2] mb-1">Designation</div>
                  <select value={designation} onChange={(e) => setDesignation(e.target.value)}
                          className={filterSel} data-testid="assoc-filter-designation">
                    <option value="">All designations</option>
                    {(meta?.designations || []).map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
                <div>
                  <div className="text-xs uppercase text-[#98A1B2] mb-1">Level</div>
                  <select value={level} onChange={(e) => setLevel(e.target.value)}
                          className={filterSel} data-testid="assoc-filter-level">
                    <option value="">All levels</option>
                    {LEVELS.map((n) => (
                      <option key={n} value={n}>{n === 4 ? `L4 / ${meta?.champs || 'CHAMPS'}` : `L${n}`}</option>
                    ))}
                    {/* How HR finds who is still waiting on an assessment.
                        Not the same query as L1 — "—" is no result yet. */}
                    <option value="none">Not assessed (—)</option>
                  </select>
                </div>
                {(q || contractorId || department || designation || level || status !== 'Active') && (
                  <Button size="sm" variant="outline"
                          onClick={() => { setQ(''); setContractorId(''); setDepartment(''); setDesignation(''); setLevel(''); setStatus('Active'); }}>
                    Clear
                  </Button>
                )}
                <div className="ml-auto text-xs text-[#98A1B2]">
                  {isAllUnits ? 'All plants' : activeUnit}
                  {data ? ` · ${data.total} row${data.total === 1 ? '' : 's'}` : ''}
                </div>
              </div>

              <div className="bg-white border border-[#E6E9F0] rounded-xl p-5">
                {missingDoj > 0 && status !== 'Left' && (
                  <p className="text-xs text-[#8A5A12] bg-[#FFF6E6] border border-[#F5DFB0] rounded-lg px-3 py-2 mb-3">
                    {missingDoj} of these have no date of joining yet — the attendance exports they came
                    from do not carry one. Fill them in with the pencil as you go; they sort to the bottom
                    of the Date of Joining column until then.
                  </p>
                )}
                {!data || !meta ? <p className="text-[#98A1B2]">Loading…</p> : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-[#EFF1F6] text-[#5A6377]">
                        <tr>
                          {COLUMNS.map((c) => (
                            <SortHeader key={c.key} col={c} sort={sort} direction={direction} onSort={onSort} />
                          ))}
                          <th className="py-2 px-2 w-24" />
                        </tr>
                      </thead>
                      <tbody>
                        {canAdd && !isAllUnits && status !== 'Left' && (
                          <AddRow meta={meta} unit={activeUnit} onAdded={refresh} />
                        )}
                        {canAdd && isAllUnits && status !== 'Left' && (
                          <tr className="bg-[#F7FAFF] border-b-2 border-[#E6E9F0]">
                            <td colSpan={9} className="py-2.5 px-2 text-xs text-[#5A6377]">
                              Pick a single plant in the header to add an associate — a new name has to
                              belong to one.
                            </td>
                          </tr>
                        )}
                        {data.rows.length === 0 ? (
                          <tr><td colSpan={9} className="py-6 text-center text-[#98A1B2]">
                            No associates match these filters.
                          </td></tr>
                        ) : data.rows.map((r) => (
                          <Row key={r.id} r={r} meta={meta} canEdit={canEdit}
                               canRemove={canRemove} onChanged={refresh}
                               onOpenProfile={(row) => setProfileId(row.id)} />
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="contractors" className="mt-4">
              <ContractorView unit={isAllUnits ? '' : (activeUnit || '')} />
            </TabsContent>

            <TabsContent value="skill" className="mt-4">
              <SkillMatrix />
            </TabsContent>
          </Tabs>
        </main>
      </div>

      {/* Mounted once at page level rather than per row: 469 rows must not
          each carry a dialog. Unmounted when closed so the next open starts
          from a clean fetch instead of the previous person's details. */}
      {profileId && (
        <AssociateProfileDialog associateId={profileId} open={!!profileId}
                                onOpenChange={(v) => { if (!v) setProfileId(null); }} />
      )}
    </div>
  );
}
